// src/App.js
import './App.css';
import React, { useEffect, useState } from 'react';
import Header from './components/Header';
import Home from './pages/Home';
import Gallery from './pages/Gallery';
import MainMenu from './pages/MainMenu';
import Footer from './components/Footer';

/**
 * TRYB ROUTERA:
 *  - false = czyste ścieżki (/menu, /gallery) — wymaga fallbacku (np. public/404.html)
 *  - true  = hash routing (/#/menu, /#/gallery) — działa wszędzie bez konfiguracji
 */
const USE_HASH_ROUTER = false;

function readRouteFromLocation() {
    const { pathname, hash, search } = window.location;

    // 1) Hash router
    if (USE_HASH_ROUTER) {
        const m = hash.match(/^#\/(.*)$/);
        const route = m ? `/${m[1]}` : '/';
        return route === '/menu' || route === '/gallery' ? route : '/';
    }

    // 2) Fallbacki: stare kotwice
    if (hash === '#gallery') return '/gallery';
    if (hash === '#menu') return '/menu';

    // 3) Wejście przez 404.html – ?p=/gallery lub ?route=/menu
    const qs = new URLSearchParams(search);
    const raw = qs.get('p') || qs.get('route');
    if (raw) {
        try {
            const decoded = decodeURIComponent(raw);
            if (decoded === '/menu' || decoded === '/gallery') return decoded;
        } catch {}
    }

    // 4) Standardowa ścieżka
    if (pathname === '/menu' || pathname === '/gallery') return pathname;
    return '/';
}

function pushRoute(to) {
    if (USE_HASH_ROUTER) {
        const targetHash = to === '/' ? '#/' : `#${to}`;
        if (window.location.hash !== targetHash) {
            window.location.hash = targetHash; // wywoła hashchange
        } else {
            window.dispatchEvent(new HashChangeEvent('hashchange'));
        }
        return;
    }
    if (window.location.pathname !== to) {
        window.history.pushState({}, '', to);
        window.dispatchEvent(new PopStateEvent('popstate'));
    }
}

// Wymusza przeliczenie layoutu (jak po scroll/resize) — pomaga na mobile
function forceLayoutRecalc() {
    // podwójny raf, by mieć pewność po wyrenderowaniu strony docelowej
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            window.dispatchEvent(new Event('resize'));
        });
    });
    // i minimalne przewinięcie do góry (iOS potrafi zachowywać offset)
    setTimeout(() => window.scrollTo(0, 0), 0);
}

export default function App() {
    const [route, setRoute] = useState(readRouteFromLocation());

    useEffect(() => {
        const onChange = () => {
            const r = readRouteFromLocation();
            setRoute(r);
            forceLayoutRecalc();
        };

        if (USE_HASH_ROUTER) {
            window.addEventListener('hashchange', onChange);
            return () => window.removeEventListener('hashchange', onChange);
        } else {
            window.addEventListener('popstate', onChange);
            window.addEventListener('hashchange', onChange); // dla starych #linków
            return () => {
                window.removeEventListener('popstate', onChange);
                window.removeEventListener('hashchange', onChange);
            };
        }
    }, []);

    // Po starcie: jeśli weszliśmy przez 404.html z ?p=..., wyczyść query i przelicz layout
    useEffect(() => {
        if (USE_HASH_ROUTER) return;
        const qs = new URLSearchParams(window.location.search);
        const raw = qs.get('p') || qs.get('route');
        if (raw) {
            try {
                const decoded = decodeURIComponent(raw);
                const target = decoded === '/menu' || decoded === '/gallery' ? decoded : '/';
                if (window.location.pathname !== target || window.location.search) {
                    window.history.replaceState({}, '', target);
                    setRoute(target);
                    forceLayoutRecalc();
                }
            } catch {
                window.history.replaceState({}, '', '/');
                setRoute('/');
                forceLayoutRecalc();
            }
        } else {
            // nawet przy normalnym starcie zrób lekkie „odświeżenie” layoutu na mobile
            forceLayoutRecalc();
        }
    }, []);

    // Globalny helper (opcjonalnie dla innych komponentów)
    window.__navigate = (to) => {
        pushRoute(to);
        forceLayoutRecalc();
    };

    return (
        <div className="App">
            <Header />
            <main id="main" role="main">
                {route === '/gallery' ? <Gallery /> :
                    route === '/menu'    ? <MainMenu /> :
                        <Home />}
            </main>
            <Footer />
        </div>
    );
}
